import React from 'react';
import { useEffect } from 'react';

import { Grid } from '@material-ui/core';
import { useTrackEvent } from '../../hooks/useTrackEvent';
import { usePermission } from '@backstage/plugin-permission-react';
import { Progress } from '@backstage/core-components';
import {
  emailServiceDefinitionPermission,
  feedCreationDefinitionPermission,
} from '@internal/plugin-permissions-common-common';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityConsumingComponentsCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  EntityAboutCard,
  EntityHasComponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
  EntityLinksCard,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { EntityGoCdContent } from '@backstage-community/plugin-gocd';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { FraudulantEmailPage } from '@internal/plugin-fraudulant-email';
import { ScriptDonwloadPage } from '@internal/plugin-script-donwload';
import { AccessDeniedPage } from '@internal/plugin-request-access-page';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item md={6}>
      <EntityAboutCard variant="gridItem" />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard variant="gridItem" height={400} />
    </Grid>

    <Grid item md={6} xs={12}>
      <EntityLinksCard />
    </Grid>

    {/* <Grid item md={6} xs={12}>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid> */}
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/go-cd" title="GoCD">
      <EntityGoCdContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    {/* <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route> */}

    <EntityLayout.Route path="/docs" title="Usage">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const WebsiteEntityPage = () => {
  const { entity } = useEntity();

  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        {overviewContent}
      </EntityLayout.Route>

      {entity.metadata.name !== 'genai-chatbot-webapp' && (
        <EntityLayout.Route path="/go-cd" title="GoCD">
          <EntityGoCdContent />
        </EntityLayout.Route>
      )}

      {/* <EntityLayout.Route path="/dependencies" title="Dependencies">
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={6}>
            <EntityDependsOnComponentsCard variant="gridItem" />
          </Grid>
          <Grid item md={6}>
            <EntityDependsOnResourcesCard variant="gridItem" />
          </Grid>
        </Grid>
      </EntityLayout.Route> */}

      {entity.metadata.name === 'genai-chatbot-webapp' && (
        <EntityLayout.Route path="/docs" title="Usage">
          {techdocsContent}
        </EntityLayout.Route>
      )}
    </EntityLayout>
  );
};

const DefaultEntityPage = () => {
  const { entity } = useEntity();

  const isScriptDownload = entity.metadata.name === 'Data-Generation-Notebook';
  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        {overviewContent}
      </EntityLayout.Route>

      {isScriptDownload && (
        <EntityLayout.Route path="/script-download" title="Download">
          <ScriptDonwloadPage />
        </EntityLayout.Route>
      )}

      <EntityLayout.Route path="/docs" title="Usage">
        {techdocsContent}
      </EntityLayout.Route>
    </EntityLayout>
  );
};

const infrastructureEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>

        <Grid item md={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      <WebsiteEntityPage />
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('infrastructure')}>
      {infrastructureEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <DefaultEntityPage />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const ApiPage = () => {
  const { entity } = useEntity();

  const isFraudulent = entity.metadata.name === 'Fraudulent-Emails';
  const isFeedCreate = entity.metadata.name === 'Feed-Creation';
  const isEmailService = entity.metadata.name === 'Email-Service';
  const isChatOps = entity.metadata.name === 'CHATOPS-API';

  const showTryItOut = isFraudulent;
  const showDocs = isFraudulent || isFeedCreate || isEmailService || isChatOps;

  const emailPermission = isEmailService
    ? usePermission({ permission: emailServiceDefinitionPermission })
    : undefined;

  const feedPermission = isFeedCreate
    ? usePermission({ permission: feedCreationDefinitionPermission })
    : undefined;

  const loading = emailPermission?.loading || feedPermission?.loading || false;

  const isDefinitionAllowed =
    (!isEmailService && !isFeedCreate) ||
    (isEmailService && emailPermission?.allowed) ||
    (isFeedCreate && feedPermission?.allowed);

  const trackEvent = useTrackEvent();

  useEffect(() => {
    const isDefinitionTab = location.pathname.endsWith('/definition');

    if (isDefinitionTab && (isEmailService || isFeedCreate)) {
      const isEmail = isEmailService;

      const attributes = {
        service: isEmail ? 'email_service' : 'feed_creation',
      };

      const context = {
        pluginId: 'marketplace',
        routeRef: '/marketplace',
        extension: isEmail ? 'EmailDefinitionTab' : 'FeedCreateDefinitionTab',
      };

      trackEvent('marketplace_click', 'definition', attributes, context);
    }
  }, [location.pathname, isEmailService, isFeedCreate]);

  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        <Grid container spacing={3}>
          {entityWarningContent}
          <Grid item md={6}>
            <EntityAboutCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <EntityCatalogGraphCard variant="gridItem" height={233} />
          </Grid>
          <Grid container item md={12}>
            <Grid item md={6}>
              <EntityProvidingComponentsCard />
            </Grid>
            <Grid item md={6}>
              <EntityConsumingComponentsCard />
            </Grid>
          </Grid>
        </Grid>
      </EntityLayout.Route>

      {isFeedCreate && (
        <EntityLayout.Route path="/go-cd" title="GoCD">
          <EntityGoCdContent />
        </EntityLayout.Route>
      )}

      {showTryItOut && (
        <EntityLayout.Route path="/try" title="Try it out">
          <FraudulantEmailPage />
        </EntityLayout.Route>
      )}

      {!showTryItOut && !isChatOps && (
        <EntityLayout.Route path="/definition" title="Definition">
          {loading ? (
            <Progress />
          ) : !isDefinitionAllowed ? (
            <AccessDeniedPage />
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <EntityApiDefinitionCard />
              </Grid>
            </Grid>
          )}
        </EntityLayout.Route>
      )}

      {showDocs && (
        <EntityLayout.Route path="/docs" title="Usage">
          {techdocsContent}
        </EntityLayout.Route>
      )}
    </EntityLayout>
  );
};

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        {/* <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid> */}
        <Grid item md={12}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={12}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        {/* <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid> */}
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Usage">
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')}>
      {componentPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isKind('api')}>
      <ApiPage />
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isKind('group')}>{groupPage}</EntitySwitch.Case>

    <EntitySwitch.Case if={isKind('user')}>{userPage}</EntitySwitch.Case>

    <EntitySwitch.Case if={isKind('system')}>{systemPage}</EntitySwitch.Case>

    <EntitySwitch.Case if={isKind('domain')}>{domainPage}</EntitySwitch.Case>

    <EntitySwitch.Case>
      <DefaultEntityPage />
    </EntitySwitch.Case>
  </EntitySwitch>
);
